import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchBuddies,
  fetchRecipes,
  generateShoppingList,
  saveMealPlan,
  getMyPlan,
  saveMyPlan,
  refreshBuddyRecipe,
} from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AccessGate from "../components/AccessGate"; // eslint-disable-line no-unused-vars -- kept for post-launch toggle back to purchase gating
import EmailGate from "../components/EmailGate";
import CookDayWizard from "../components/CookDayWizard";
import { useAuth } from "../lib/auth";
import {
  Sparkles,
  Trash2,
  RotateCcw,
  ShoppingCart,
  Check,
  Loader2,
  Mail,
  Printer,
  Wand2,
  BookOpen,
  ChevronDown,
  Clock,
  Utensils,
  ChefHat,
  Cloud,
  LogIn,
  RefreshCw,
  ArrowDown,
  X,
} from "lucide-react";
import { toast } from "sonner";
import useSeo from "../lib/useSeo";
import { trackEvent } from "../lib/analytics";

const DAYS = [
  { id: "monday", short: "Mon", full: "Monday" },
  { id: "tuesday", short: "Tue", full: "Tuesday" },
  { id: "wednesday", short: "Wed", full: "Wednesday" },
  { id: "thursday", short: "Thu", full: "Thursday" },
  { id: "friday", short: "Fri", full: "Friday" },
  { id: "saturday", short: "Sat", full: "Saturday" },
  { id: "sunday", short: "Sun", full: "Sunday" },
];

const STORAGE_KEY = "platebuddies_meal_plan_v1";
const CUSTOM_KEY = "platebuddies_custom_buddies_v1";
// One-time flags so the coach mark & sign-in nudge don't reappear forever.
const NUDGE_SEEN_KEY = "platebuddies_planner_nudge_seen_v1";
const SAVE_PROMPT_KEY = "platebuddies_planner_save_prompt_seen_v1";
// Hero A/B variant (session-scoped so a user doesn't see it flip mid-session).
const HERO_VARIANT_KEY = "platebuddies_planner_hero_variant";
const COOKDAY_NUDGE_KEY = "platebuddies_planner_cookday_nudge_seen_v1";
const HERO_VARIANTS = {
  benefit: {
    accent: "sorted in 60 seconds.",
    prefix: "Dinner for the week,",
  },
  noun: {
    accent: "done in 60 seconds.",
    prefix: "This week's dinner,",
  },
};

function loadCustomBuddies() {
  try {
    return JSON.parse(localStorage.getItem(CUSTOM_KEY) || "[]");
  } catch {
    return [];
  }
}

export default function MealPlanner() {
  const { user, status, loginUrl } = useAuth();
  const [buddies, setBuddies] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [customBuddies, setCustomBuddies] = useState(loadCustomBuddies);
  const [plan, setPlan] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {
      /* noop */
    }
    return DAYS.reduce((acc, d) => ({ ...acc, [d.id]: null }), {});
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const [list, setList] = useState(null);
  const [crossedOff, setCrossedOff] = useState(() => new Set());
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [cookDayOpen, setCookDayOpen] = useState(false);
  const [cookDayStart, setCookDayStart] = useState(0);
  // Cross-device sync state — only meaningful when the user is signed in.
  const [cloudStatus, setCloudStatus] = useState("idle"); // "idle" | "loading" | "saving" | "saved" | "error"
  const initialLoadRef = useRef(false);
  const saveTimerRef = useRef(null);

  // ---- Onboarding & conversion nudges ----
  // Hero A/B variant — picked once per session on first mount, tracked to GA4
  // so we can compare CTR + downstream conversion between noun-first vs
  // benefit-first framing. Kept in sessionStorage so the variant stays stable
  // across a single browsing session even if the user reloads.
  const [heroVariant, setHeroVariant] = useState("benefit");
  // First-time coach-mark ("tap a day → then a Buddy"). Suppressed after
  // any interaction so it never nags returning visitors.
  const [showNudge, setShowNudge] = useState(false);
  // Guest sign-in prompt — surfaced once, after the user commits to 3 days.
  // Dismisses locally so we don't badger them the rest of the session.
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [savePromptDismissed, setSavePromptDismissed] = useState(false);
  // Cook-day coach mark — appears once, the first time the user generates
  // a shopping list, so they discover the recipe-by-recipe kitchen wizard.
  const [showCookDayNudge, setShowCookDayNudge] = useState(false);

  useSeo({
    title: "Weekly Meal Planner — Plate Buddies",
    description:
      "Plan your family's whole week in 60 seconds. Drag Plate Buddies onto each day, auto-build a shopping list, and email the plan to yourself.",
    url: "https://platebuddies.com/planner",
  });

  useEffect(() => {
    fetchBuddies().then(setBuddies).catch((err) => console.warn("[fetch] failed:", err));
    fetchRecipes().then(setRecipes).catch((err) => console.warn("[fetch] failed:", err));
    window.scrollTo(0, 0);
  }, []);

  // Pick a hero copy variant on first mount (persists for the session).
  // Also show the first-time coach mark to guests who've never planned before.
  useEffect(() => {
    try {
      let v = sessionStorage.getItem(HERO_VARIANT_KEY);
      if (!v || !HERO_VARIANTS[v]) {
        v = Math.random() < 0.5 ? "benefit" : "noun";
        sessionStorage.setItem(HERO_VARIANT_KEY, v);
      }
      setHeroVariant(v);
      trackEvent("planner_hero_variant_view", { variant: v });
    } catch {
      /* noop — sessionStorage unavailable, fall back to default */
    }
    try {
      if (!localStorage.getItem(NUDGE_SEEN_KEY)) {
        // Show the arrow after a beat so it feels intentional, not startling.
        const t = setTimeout(() => setShowNudge(true), 900);
        return () => clearTimeout(t);
      }
    } catch {
      /* noop */
    }
  }, []);

  const dismissNudge = () => {
    if (!showNudge) return;
    setShowNudge(false);
    try {
      localStorage.setItem(NUDGE_SEEN_KEY, "1");
    } catch {
      /* noop */
    }
  };

  // If the user clicked "Plan a meal" on a Library card, the buddy id is queued
  // in localStorage. Drop it on Monday and pop the queue so it doesn't repeat.
  useEffect(() => {
    if (customBuddies.length === 0) return;
    let raw;
    try {
      raw = localStorage.getItem("platebuddies_planner_queue_v1");
    } catch {
      return;
    }
    if (!raw) return;
    try {
      const { buddyId } = JSON.parse(raw);
      if (buddyId && customBuddies.some((b) => b.id === buddyId)) {
        setPlan((p) => ({ ...p, monday: `custom:${buddyId}` }));
        toast.success("Your Buddy is on Monday — pick another day to keep going.");
      }
    } catch {
      /* noop */
    }
    try {
      localStorage.removeItem("platebuddies_planner_queue_v1");
    } catch {
      /* noop */
    }
  }, [customBuddies]);

  // Re-sync custom buddies whenever this tab regains focus or another tab
  // updates the library — covers the case where a buddy is created on
  // /create in another tab/window and the user comes back to /planner.
  useEffect(() => {
    const refresh = () => setCustomBuddies(loadCustomBuddies());
    const onStorage = (e) => {
      if (e.key === CUSTOM_KEY) refresh();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
    } catch {
      /* noop */
    }
  }, [plan]);

  // Prefill the "email me my plan" input with the signed-in user's email.
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user, email]);

  // Pull the user's saved plan from the server on sign-in. Runs once per
  // authed session — after that we rely on the debounced writer below.
  useEffect(() => {
    if (status !== "authed" || initialLoadRef.current) return;
    initialLoadRef.current = true;
    setCloudStatus("loading");
    getMyPlan()
      .then((data) => {
        const hasAny =
          data?.days && Object.values(data.days).some((v) => !!v);
        if (hasAny) {
          setPlan(data.days);
          if (Array.isArray(data.items_have) && data.items_have.length) {
            setCrossedOff(new Set(data.items_have));
          }
          toast.success("Loaded your saved plan.");
        }
        setCloudStatus("saved");
      })
      .catch(() => {
        setCloudStatus("error");
      });
  }, [status]);

  // Debounced push to the server whenever a signed-in user's plan changes.
  useEffect(() => {
    if (status !== "authed") return;
    if (!initialLoadRef.current) return; // wait until initial load resolves
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setCloudStatus("saving");
    saveTimerRef.current = setTimeout(() => {
      saveMyPlan({
        days: plan,
        custom_buddies: scheduledCustomBuddies,
        items_have: Array.from(crossedOff),
      })
        .then(() => setCloudStatus("saved"))
        .catch(() => setCloudStatus("error"));
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, crossedOff, status]);

  const assigned = useMemo(
    () => Object.values(plan).filter(Boolean).length,
    [plan]
  );

  // Once a guest hits 3 days planned, surface the sign-in prompt — this is
  // the moment they've invested enough that losing the plan would hurt.
  // Only fires once per browser (stored in localStorage) and never for signed-in users.
  useEffect(() => {
    if (status === "authed") return;
    if (savePromptDismissed) return;
    if (assigned < 3) return;
    try {
      if (localStorage.getItem(SAVE_PROMPT_KEY)) return;
    } catch {
      /* noop */
    }
    setShowSavePrompt(true);
    trackEvent("planner_save_prompt_shown", { assigned });
  }, [assigned, status, savePromptDismissed]);

  const dismissSavePrompt = () => {
    setShowSavePrompt(false);
    setSavePromptDismissed(true);
    try {
      localStorage.setItem(SAVE_PROMPT_KEY, "1");
    } catch {
      /* noop */
    }
    trackEvent("planner_save_prompt_dismissed");
  };

  const assignBuddy = (buddyId) => {
    dismissNudge();
    if (!selectedDay) {
      toast.info("Pick a day first, then choose a Buddy.");
      return;
    }
    setPlan((p) => ({ ...p, [selectedDay]: buddyId }));
    // Advance to next empty day for a smoother flow
    const idx = DAYS.findIndex((d) => d.id === selectedDay);
    const nextEmpty = DAYS.slice(idx + 1).find((d) => !plan[d.id]);
    setSelectedDay(nextEmpty ? nextEmpty.id : null);
  };

  const assignCustomBuddy = (cb) => {
    dismissNudge();
    if (!selectedDay) {
      toast.info("Pick a day first, then choose a Buddy.");
      return;
    }
    // Custom buddies are stored as the slot value `custom:<id>` so the parser
    // resolves them back from the customBuddies library.
    setPlan((p) => ({ ...p, [selectedDay]: `custom:${cb.id}` }));
    const idx = DAYS.findIndex((d) => d.id === selectedDay);
    const nextEmpty = DAYS.slice(idx + 1).find((d) => !plan[d.id]);
    setSelectedDay(nextEmpty ? nextEmpty.id : null);
  };

  const removeCustomBuddy = (id) => {
    const next = customBuddies.filter((b) => b.id !== id);
    setCustomBuddies(next);
    try {
      localStorage.setItem(CUSTOM_KEY, JSON.stringify(next));
    } catch {
      /* noop */
    }
    // Also drop from any planned days that referenced this buddy.
    setPlan((p) => {
      const out = { ...p };
      Object.keys(out).forEach((day) => {
        if (out[day] === `custom:${id}`) out[day] = null;
      });
      return out;
    });
    toast.success("Buddy removed.");
  };

  const clearDay = (dayId) => setPlan((p) => ({ ...p, [dayId]: null }));

  const clearAll = () => {
    setPlan(DAYS.reduce((acc, d) => ({ ...acc, [d.id]: null }), {}));
    setList(null);
    setSelectedDay(null);
  };

  const autoFill = () => {
    if (buddies.length === 0) return;
    const shuffled = [...buddies].sort(() => Math.random() - 0.5);
    const next = {};
    DAYS.forEach((d, i) => {
      next[d.id] = shuffled[i % shuffled.length].id;
    });
    setPlan(next);
    setSelectedDay(null);
    setList(null);
    toast.success("Week filled with a random lineup — swap any day if you like.");
  };

  const buildList = async () => {
    if (assigned === 0) {
      toast.error("Add at least one Buddy to your week first.");
      return;
    }
    setGenerating(true);
    try {
      const data = await generateShoppingList(plan, scheduledCustomBuddies);
      setList(data);
      // Reset the "already have" toggles whenever a fresh list is built.
      setCrossedOff(new Set());
      // Surface the Cook-Day coach mark the first time a list is built —
      // this is the moment the wizard's value clicks (list in one hand,
      // recipes in the other). Auto-hides after 6 seconds so it doesn't linger.
      try {
        if (!localStorage.getItem(COOKDAY_NUDGE_KEY)) {
          setTimeout(() => setShowCookDayNudge(true), 900);
          setTimeout(() => dismissCookDayNudge(), 7200);
        }
      } catch {
        /* noop */
      }
      // Scroll to list
      setTimeout(() => {
        document.getElementById("shopping-list")?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    } catch {
      toast.error("Couldn't build the shopping list. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  const dismissCookDayNudge = () => {
    setShowCookDayNudge(false);
    try {
      localStorage.setItem(COOKDAY_NUDGE_KEY, "1");
    } catch {
      /* noop */
    }
  };

  const openCookDay = (startIdx = 0) => {
    if (scheduledDays.length === 0) {
      toast.error("Add at least one Buddy to your week first.");
      return;
    }
    setCookDayStart(startIdx);
    setCookDayOpen(true);
  };

  // Track which custom Buddy is currently being refreshed so the row shows
  // a spinner instead of the pencil icon.
  const [refreshingId, setRefreshingId] = useState(null);

  /**
   * Regenerate the recipe on a custom Buddy — fixes legacy Buddies (like the
   * pre-fix Meatloaf Matt) whose localStorage entry still holds a mismatched
   * template recipe. Server returns the current, nutritionally-balanced
   * recipe; we mirror it into localStorage + in-memory state so the meal
   * planner peek + shopping list + cook-day wizard all pick up the fix
   * without a page reload.
   */
  const refreshCustomRecipe = async (buddyId) => {
    if (!buddyId) return;
    setRefreshingId(buddyId);
    try {
      const { recipe } = await refreshBuddyRecipe(buddyId);
      if (!recipe) {
        toast.error("Couldn't refresh — try again in a moment.");
        return;
      }
      // 1. Update in-memory list.
      const updated = customBuddies.map((cb) =>
        cb.id === buddyId ? { ...cb, recipe } : cb
      );
      setCustomBuddies(updated);
      // 2. Mirror to localStorage so the fix survives a reload.
      try {
        localStorage.setItem(CUSTOM_KEY, JSON.stringify(updated));
      } catch {
        /* noop */
      }
      // 3. Reset the shopping list — old ingredients no longer apply.
      setList(null);
      toast.success(`Refreshed — "${recipe.title}" is ready.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't refresh. Try again.";
      toast.error(msg);
    } finally {
      setRefreshingId(null);
    }
  };

  const toggleCrossed = (name) => {
    setCrossedOff((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const printNeeded = () => {
    // Honor the user's request: print only the items they still need.
    // Trigger native print after a tick so the print-only CSS rules apply.
    setTimeout(() => window.print(), 50);
  };

  const emailPlan = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setSaving(true);
    try {
      // Send the FULL list, with the user's "already-have" annotations so the
      // email can render owned items with a strikethrough (still visible).
      const items_have = list ? Array.from(crossedOff) : [];
      await saveMealPlan(plan, email, scheduledCustomBuddies, items_have);
      toast.success("Plan emailed! Check your inbox.");
      setEmail("");
    } catch {
      toast.error("Couldn't send the plan. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const buddyById = (id) => buddies.find((b) => b.id === id);
  const customById = (id) => customBuddies.find((b) => b.id === id);
  const parseSlot = (val) => {
    if (!val) return { type: "empty" };
    if (typeof val === "string" && val.startsWith("custom:")) {
      const key = val.slice(7);
      const cb = customById(key);
      if (cb) return { type: "custom_buddy", buddy: cb };
      return { type: "custom", name: key };
    }
    return { type: "buddy", buddy: buddyById(val) };
  };

  // Build the array of custom buddies actually scheduled this week (with ingredients)
  const scheduledCustomBuddies = useMemo(() => {
    const ids = new Set(
      Object.values(plan)
        .filter((v) => typeof v === "string" && v.startsWith("custom:"))
        .map((v) => v.slice(7))
    );
    return customBuddies
      .filter((cb) => ids.has(cb.id))
      .map((cb) => ({
        id: cb.id,
        name: cb.name,
        ingredients: cb.recipe?.ingredients || [],
      }));
  }, [plan, customBuddies]);

  // Resolved per-day schedule (Mon → Sun) used by the printable cookbook view.
  const scheduledDays = useMemo(() => {
    const out = [];
    DAYS.forEach((d) => {
      const slot = parseSlot(plan[d.id]);
      if (slot.type === "buddy" && slot.buddy) {
        const recipe = recipes.find((r) => r.buddy_id === slot.buddy.id);
        if (recipe) {
          out.push({
            day: d.full,
            buddyName: slot.buddy.name,
            recipe,
            recipe_id: recipe.id,
            isCustom: false,
          });
        }
      } else if (slot.type === "custom_buddy" && slot.buddy) {
        out.push({
          day: d.full,
          buddyName: slot.buddy.name,
          buddy_id: slot.buddy.id,
          recipe: slot.buddy.recipe || { title: slot.buddy.name, ingredients: [] },
          isCustom: true,
        });
      }
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, recipes, customBuddies]);

  const printRecipes = () => {
    if (scheduledDays.length === 0) {
      toast.error("Add at least one Buddy to your week first.");
      return;
    }
    document.body.classList.add("print-mode-recipes");
    setTimeout(() => {
      window.print();
      // Strip the marker class after the print dialog closes.
      setTimeout(() => document.body.classList.remove("print-mode-recipes"), 200);
    }, 50);
  };

  return (
    <div data-testid="page-planner" className="font-body bg-pb-cream min-h-screen">
      <Navbar />
      <EmailGate
        feature="planner"
        title="Unlock the Meal Planner"
        blurb="Drag your Buddies onto each day of the week, get a smart shopping list, and turn dinner planning into a 60-second family ritual."
        source="meal_planner"
      >

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="blob bg-pb-sky" style={{ width: 460, height: 460, top: -120, left: -100, opacity: 0.45 }} />
        <div className="blob bg-pb-sun" style={{ width: 360, height: 360, top: 40, right: -100, opacity: 0.45 }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-10 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-pb-coral border border-pb-coral/20">
            <Sparkles className="h-3.5 w-3.5" /> Free weekly meal plan
          </span>
          <h1
            className="font-display font-black tracking-tight text-pb-ink mt-5 text-5xl md:text-6xl leading-[0.95]"
            data-testid={`planner-hero-h1-${heroVariant}`}
          >
            {HERO_VARIANTS[heroVariant].prefix}{" "}
            <span className="wavy">{HERO_VARIANTS[heroVariant].accent}</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-lg text-pb-soft">
            Pick a Buddy for each day. We'll build the shopping list, save the recipes, and hand you a game plan you can print or email. No more 4:47 PM fridge stare.
          </p>

          {/* Sync ribbon — visible only to signed-in users so they see live
              save status. Guests get a sticky footer prompt after 3+ dinners
              instead (loss-aversion moment), so we don't stack two CTAs. */}
          {status === "authed" && user && (
            <div className="mt-6 flex justify-center">
              <div
                data-testid="planner-sync-badge"
                className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-pb-green/30 px-4 py-1.5 text-xs font-extrabold text-pb-ink shadow-sm"
              >
                <Cloud className="h-3.5 w-3.5 text-pb-green" />
                {cloudStatus === "loading" && "Loading your plan…"}
                {cloudStatus === "saving" && "Saving…"}
                {cloudStatus === "saved" && `Synced as ${user.email}`}
                {cloudStatus === "error" && "Sync paused — we'll retry"}
                {cloudStatus === "idle" && `Signed in as ${user.email}`}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Save-progress sticky footer — only for guests who've committed to
          3+ dinners (loss-aversion moment). Sits at the bottom of the
          viewport so it's always one tap away without stealing hero space.
          Dismissable, tracked, remembered locally. */}
      {showSavePrompt && (
        <div
          data-testid="planner-save-prompt"
          role="complementary"
          aria-label="Save your plan"
          style={{ animation: "fadeInUp 320ms ease-out both" }}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-1.5rem)] max-w-xl no-print"
        >
          <div className="flex items-center gap-3 rounded-2xl bg-pb-ink/95 backdrop-blur-md text-white px-4 py-3 shadow-[0_20px_50px_rgba(20,25,35,0.28)] border border-white/10">
            <span className="hidden sm:inline-flex h-9 w-9 rounded-xl bg-pb-coral/20 items-center justify-center flex-shrink-0">
              <Cloud className="h-4 w-4 text-pb-coral" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-extrabold text-sm leading-snug">
                {assigned} dinners locked in — save your plan?
              </p>
              <p className="text-white/60 text-xs mt-0.5">
                One click. Access it on any device.
              </p>
            </div>
            <a
              href={loginUrl}
              data-testid="planner-save-prompt-cta"
              onClick={() => trackEvent("planner_save_prompt_click", { assigned })}
              className="inline-flex items-center gap-1.5 rounded-full bg-pb-coral text-white font-extrabold text-sm px-4 py-2 hover:bg-white hover:text-pb-coral transition flex-shrink-0"
            >
              Save my plan
            </a>
            <button
              onClick={dismissSavePrompt}
              data-testid="planner-save-prompt-dismiss"
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition flex-shrink-0"
              aria-label="Dismiss save prompt"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Week planning board */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-10">
        <div className="bg-white rounded-[2.5rem] p-6 sm:p-10 shadow-[0_24px_60px_rgba(28,22,40,0.08)] border border-pb-sky/30">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display font-black text-2xl">Your week</h2>
              <p className="text-sm text-pb-soft mt-1">
                {assigned}/7 days planned
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="btn-autofill"
                onClick={autoFill}
                className="inline-flex items-center gap-2 rounded-full bg-pb-sky/60 text-pb-ink font-bold px-4 py-2 hover:bg-pb-aqua hover:text-white transition text-sm"
              >
                <Wand2 className="h-4 w-4" /> Surprise me
              </button>
              <button
                data-testid="btn-clear-all"
                onClick={clearAll}
                className="inline-flex items-center gap-2 rounded-full bg-pb-cream text-pb-ink font-bold px-4 py-2 hover:bg-pb-coral hover:text-white transition text-sm border border-pb-sky/40"
              >
                <RotateCcw className="h-4 w-4" /> Reset
              </button>
            </div>
          </div>

          {/* Day grid */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {DAYS.map((d) => {
              const slot = parseSlot(plan[d.id]);
              const isSelected = selectedDay === d.id;
              const hasContent = slot.type !== "empty";
              return (
                <button
                  key={d.id}
                  data-testid={`day-${d.id}`}
                  onClick={() => {
                    dismissNudge();
                    setSelectedDay(isSelected ? null : d.id);
                  }}
                  className={`relative rounded-3xl p-4 text-left transition-all duration-300 border-4 ${
                    isSelected
                      ? "border-pb-coral bg-pb-coral/5 -translate-y-1 shadow-[0_18px_40px_rgba(242,106,52,0.25)]"
                      : hasContent
                      ? "border-transparent shadow-[0_10px_28px_rgba(28,22,40,0.08)] hover:-translate-y-1"
                      : "border-dashed border-pb-sky/60 bg-pb-cream hover:bg-white hover:border-pb-coral/40"
                  }`}
                  style={
                    slot.type === "buddy"
                      ? { background: slot.buddy?.bg }
                      : slot.type === "custom_buddy"
                      ? { background: slot.buddy?.bg || "linear-gradient(135deg,#FFF6D6 0%,#FFE3D8 100%)" }
                      : slot.type === "custom"
                      ? { background: "linear-gradient(135deg,#FFF6D6 0%,#FFE3D8 100%)" }
                      : undefined
                  }
                >
                  <span className="inline-block text-[10px] font-extrabold uppercase tracking-widest text-pb-coral bg-white/80 rounded-full px-2 py-0.5 shadow-sm">
                    {d.short}
                  </span>
                  {slot.type === "buddy" && slot.buddy ? (
                    <div className="mt-3 flex flex-col items-center text-center">
                      <div className="h-20 w-20 rounded-full overflow-hidden bg-white border-[4px] border-white shadow-[0_8px_18px_rgba(0,0,0,0.15)]">
                        <img src={slot.buddy.image} alt={slot.buddy.name} className="w-full h-full object-cover" />
                      </div>
                      <p className="mt-2 font-display font-black text-sm leading-tight">
                        {slot.buddy.name}
                      </p>
                      <p className="text-[10px] text-pb-soft mt-0.5 leading-tight">{slot.buddy.recipe}</p>
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid={`btn-clear-${d.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          clearDay(d.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            clearDay(d.id);
                          }
                        }}
                        className="mt-2 text-[10px] inline-flex items-center gap-1 text-pb-soft hover:text-pb-coral font-bold cursor-pointer"
                      >
                        <Trash2 className="h-3 w-3" /> remove
                      </span>
                    </div>
                  ) : slot.type === "custom_buddy" && slot.buddy ? (
                    <div className="mt-3 flex flex-col items-center text-center">
                      <div className="h-20 w-20 rounded-full overflow-hidden bg-white border-[4px] border-white shadow-[0_8px_18px_rgba(0,0,0,0.15)]">
                        <img src={slot.buddy.image} alt={slot.buddy.name} className="w-full h-full object-cover" />
                      </div>
                      <p className="mt-2 font-display font-black text-sm leading-tight">
                        {slot.buddy.name}
                      </p>
                      <p className="text-[10px] text-pb-soft mt-0.5 leading-tight">
                        {slot.buddy.recipe?.title || "Custom Buddy"}
                      </p>
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid={`btn-clear-${d.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          clearDay(d.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            clearDay(d.id);
                          }
                        }}
                        className="mt-2 text-[10px] inline-flex items-center gap-1 text-pb-soft hover:text-pb-coral font-bold cursor-pointer"
                      >
                        <Trash2 className="h-3 w-3" /> remove
                      </span>
                    </div>
                  ) : slot.type === "custom" ? (
                    <div className="mt-3 flex flex-col items-center text-center">
                      <div className="h-20 w-20 rounded-full overflow-hidden bg-white border-[4px] border-white shadow-[0_8px_18px_rgba(0,0,0,0.15)] flex items-center justify-center bg-gradient-to-br from-pb-sun via-pb-coral to-pb-aqua">
                        <Sparkles className="h-8 w-8 text-white" />
                      </div>
                      <p className="mt-2 font-display font-black text-sm leading-tight break-words">
                        {slot.name}
                      </p>
                      <p className="text-[10px] text-pb-soft mt-0.5 leading-tight">Your own meal</p>
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid={`btn-clear-${d.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          clearDay(d.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            clearDay(d.id);
                          }
                        }}
                        className="mt-2 text-[10px] inline-flex items-center gap-1 text-pb-soft hover:text-pb-coral font-bold cursor-pointer"
                      >
                        <Trash2 className="h-3 w-3" /> remove
                      </span>
                    </div>
                  ) : (
                    <div className="mt-4 h-32 flex items-center justify-center text-center">
                      <p className={`text-xs font-bold ${isSelected ? "text-pb-coral" : "text-pb-soft/70"}`}>
                        {isSelected ? "Pick a Buddy ↓" : "Add a dinner"}
                      </p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* First-time coach mark — an animated arrow guiding new visitors
              from the day grid down to the Buddy tray. Dismisses on any
              interaction (day tap, Buddy tap, or the X). Only shows once ever. */}
          {showNudge && assigned === 0 && (
            <div
              data-testid="planner-first-nudge"
              className="mt-6 flex flex-col items-center justify-center"
              style={{ animation: "fadeInUp 400ms ease-out both" }}
            >
              <div className="relative inline-flex items-center gap-3 rounded-full bg-pb-coral text-white px-5 py-2.5 shadow-[0_12px_28px_rgba(242,106,52,0.35)]">
                <span className="text-sm font-extrabold">
                  Tap any day above <span className="hidden sm:inline">↑</span>, then pick a Buddy below
                </span>
                <button
                  onClick={dismissNudge}
                  data-testid="planner-first-nudge-dismiss"
                  className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-white/20 hover:bg-white/30 transition"
                  aria-label="Dismiss tip"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <ArrowDown
                className="h-6 w-6 text-pb-coral mt-2 animate-bounce"
                aria-hidden="true"
              />
            </div>
          )}

          {/* Buddy picker tray */}
          <div className="mt-8 pt-6 border-t-2 border-dashed border-pb-sky/40">
            <p className="text-xs font-extrabold uppercase tracking-widest text-pb-soft text-center">
              Pick a Buddy — we'll add it to the day you selected
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {buddies.map((b) => (
                <button
                  key={b.id}
                  data-testid={`magnet-${b.id}`}
                  onClick={() => assignBuddy(b.id)}
                  className={`group flex items-center gap-2 bg-white rounded-full pl-1 pr-4 py-1 border-2 shadow-md hover:-translate-y-1 transition-all ${
                    selectedDay ? "border-pb-coral/30 hover:border-pb-coral" : "border-transparent opacity-90"
                  }`}
                >
                  <span className="h-10 w-10 rounded-full overflow-hidden bg-white border-2 border-white">
                    <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                  </span>
                  <span className="font-extrabold text-sm">{b.name}</span>
                </button>
              ))}

              {customBuddies.map((cb) => (
                <div
                  key={cb.id}
                  className={`group relative flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border-2 shadow-md hover:-translate-y-1 transition-all bg-white ${
                    selectedDay ? "border-pb-coral/30 hover:border-pb-coral" : "border-transparent opacity-90"
                  }`}
                  data-testid={`magnet-custom-${cb.id}`}
                >
                  <button
                    onClick={() => assignCustomBuddy(cb)}
                    className="flex items-center gap-2"
                  >
                    <span className="h-10 w-10 rounded-full overflow-hidden bg-white border-2 border-white relative">
                      <img src={cb.image} alt={cb.name} className="w-full h-full object-cover" />
                      <span className="absolute -top-1 -right-1 bg-pb-coral text-white text-[8px] font-extrabold rounded-full px-1.5 py-0.5">
                        YOURS
                      </span>
                    </span>
                    <span className="font-extrabold text-sm">{cb.name}</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeCustomBuddy(cb.id);
                    }}
                    className="text-pb-soft hover:text-pb-coral ml-1"
                    aria-label={`Remove ${cb.name}`}
                    data-testid={`remove-magnet-${cb.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}

              <Link
                to="/create"
                data-testid="magnet-create-link"
                className="group flex items-center gap-2 rounded-full pl-1 pr-4 py-1 border-2 border-dashed shadow-md hover:-translate-y-1 transition-all bg-white text-pb-coral border-pb-coral/40 hover:border-pb-coral hover:bg-pb-coral hover:text-white"
              >
                <span className="h-10 w-10 rounded-full bg-pb-cream border-2 border-white flex items-center justify-center group-hover:bg-white">
                  <Sparkles className="h-4 w-4" />
                </span>
                <span className="font-extrabold text-sm">Make a Buddy</span>
              </Link>
            </div>
            {!selectedDay && assigned < 7 && (
              <p className="text-center text-xs text-pb-soft mt-4">
                Tap a day above to get started.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Shopping list builder */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <div className="bg-pb-ink text-white rounded-[2.5rem] p-8 sm:p-12 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <p className="text-xs font-extrabold uppercase tracking-widest text-pb-sun">Shopping list · ready in seconds</p>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-2 leading-tight">
              One list. Every ingredient. Zero backtracking.
            </h2>
            <p className="text-white/80 mt-3 max-w-xl">
              We roll up your whole week into a single grocery run — combined, deduped, and sorted by store aisle. No forgotten cheddar. No pantry double-buys. No standing on aisle 4 wondering why you're there.
            </p>
            <p className="text-white/60 mt-3 max-w-xl text-xs">
              💡 Print it, email it, or <b className="text-pb-sun">sign in</b> and your list follows you to the store on your phone.
            </p>
          </div>
          <div className="flex md:justify-end gap-3 flex-wrap relative">
            {/* Cook-day coach mark — shows once, the first time a shopping
                list is generated. Positioned above the Cook Day button so
                it's clear which control it's pointing to. */}
            {showCookDayNudge && (
              <div
                data-testid="planner-cookday-nudge"
                style={{ animation: "fadeInUp 320ms ease-out both" }}
                className="pointer-events-none absolute -top-14 right-0 z-10 hidden md:block"
              >
                <div className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-pb-sun text-pb-ink text-xs font-extrabold px-3 py-2 shadow-[0_10px_24px_rgba(0,0,0,0.18)]">
                  <ChefHat className="h-3 w-3" />
                  New: step-by-step in the kitchen
                  <button
                    onClick={dismissCookDayNudge}
                    aria-label="Dismiss tip"
                    className="inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-pb-ink/10 transition"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="mt-1 mr-14 flex justify-end">
                  <ArrowDown className="h-4 w-4 text-pb-sun animate-bounce" aria-hidden="true" />
                </div>
              </div>
            )}
            <button
              data-testid="btn-cook-day"
              onClick={() => {
                dismissCookDayNudge();
                openCookDay(0);
              }}
              disabled={scheduledDays.length === 0}
              className={`inline-flex items-center gap-2 rounded-full bg-pb-sun text-pb-ink font-extrabold px-6 py-4 hover:bg-white transition disabled:opacity-50 ${
                showCookDayNudge ? "ring-4 ring-pb-sun/50 ring-offset-2 ring-offset-pb-ink" : ""
              }`}
            >
              <ChefHat className="h-4 w-4" />
              Cook day mode
            </button>
            <button
              data-testid="btn-print-recipes"
              onClick={printRecipes}
              disabled={assigned === 0}
              className="inline-flex items-center gap-2 rounded-full bg-white text-pb-ink font-extrabold px-6 py-4 hover:bg-pb-sun transition disabled:opacity-50"
            >
              <BookOpen className="h-4 w-4" />
              Print recipes
            </button>
            <button
              data-testid="btn-generate-list"
              onClick={buildList}
              disabled={generating || assigned === 0}
              className="inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-7 py-4 shadow-[0_12px_28px_rgba(242,106,52,0.4)] hover:scale-105 transition disabled:opacity-50 disabled:hover:scale-100"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
              {generating ? "Building…" : "Generate shopping list"}
            </button>
          </div>
        </div>
      </section>

      {/* Recipe peek — surfaces each scheduled day's recipe inline so families
          can browse what they've planned without leaving the page. */}
      {scheduledDays.length > 0 && (
        <section
          data-testid="recipe-peek"
          className="max-w-5xl mx-auto px-5 sm:px-8 pb-10 no-print"
        >
          <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
                This week's dinners
              </p>
              <h3 className="font-display font-black text-2xl sm:text-3xl mt-1 leading-tight">
                Every recipe, ready when you are.
              </h3>
            </div>
            <p className="text-sm text-pb-soft">
              Tap a card to expand · {scheduledDays.length} {scheduledDays.length === 1 ? "recipe" : "recipes"}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {scheduledDays.map((entry, idx) => {
              const isOpen = expandedRecipe === idx;
              const recipe = entry.recipe || {};
              return (
                <div
                  key={`${entry.day}-${idx}`}
                  data-testid={`recipe-peek-${idx}`}
                  className={`rounded-2xl bg-white border-2 transition-all overflow-hidden ${
                    isOpen ? "border-pb-coral shadow-lg" : "border-pb-sky/30 shadow-sm hover:border-pb-coral/40"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setExpandedRecipe(isOpen ? null : idx)}
                    className="w-full flex items-center gap-3 p-4 text-left"
                    data-testid={`recipe-peek-toggle-${idx}`}
                  >
                    <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-pb-cream flex items-center justify-center">
                      <Utensils className="h-5 w-5 text-pb-coral" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-pb-coral">
                        {entry.day} · {entry.buddyName}
                      </div>
                      <p className="font-display font-black text-base sm:text-lg leading-tight truncate">
                        {recipe.title || entry.buddyName}
                      </p>
                      {recipe.time && (
                        <p className="text-[11px] text-pb-soft mt-0.5 inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {recipe.time}
                        </p>
                      )}
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-pb-soft flex-shrink-0 transition-transform ${
                        isOpen ? "rotate-180 text-pb-coral" : ""
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-pb-sky/20 pt-3 text-sm">
                      {recipe.story && (
                        <p className="italic text-pb-soft mb-3">{recipe.story}</p>
                      )}
                      {recipe.ingredients?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-coral mb-1">
                            Ingredients
                          </p>
                          <ul className="text-pb-ink space-y-0.5">
                            {recipe.ingredients.slice(0, 6).map((ing, i) => (
                              <li key={i}>• {ing}</li>
                            ))}
                            {recipe.ingredients.length > 6 && (
                              <li className="text-pb-soft">
                                + {recipe.ingredients.length - 6} more in the full recipe
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      {recipe.steps?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-coral mb-1">
                            Steps
                          </p>
                          <ol className="list-decimal list-inside space-y-1 marker:text-pb-coral marker:font-bold">
                            {recipe.steps.slice(0, 3).map((s, i) => (
                              <li key={i} className="text-pb-ink">{s}</li>
                            ))}
                            {recipe.steps.length > 3 && (
                              <li className="list-none text-pb-soft">
                                + {recipe.steps.length - 3} more steps
                              </li>
                            )}
                          </ol>
                        </div>
                      )}
                      {recipe.kid_task && (
                        <div className="mb-3 p-2.5 rounded-xl bg-pb-sky/20 text-pb-ink text-xs">
                          <b className="text-pb-coral">Kid task:</b> {recipe.kid_task}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCookDay(idx);
                          }}
                          data-testid={`recipe-peek-cook-${idx}`}
                          className="inline-flex items-center gap-1.5 text-xs font-extrabold text-white bg-pb-coral rounded-full px-3 py-1.5 hover:bg-pb-ink transition"
                        >
                          <ChefHat className="h-3 w-3" /> Cook this
                        </button>
                        {entry.isCustom && entry.buddy_id && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              refreshCustomRecipe(entry.buddy_id);
                            }}
                            disabled={refreshingId === entry.buddy_id}
                            data-testid={`recipe-peek-refresh-${idx}`}
                            className="inline-flex items-center gap-1.5 text-xs font-extrabold text-pb-coral bg-white border-2 border-pb-coral/30 rounded-full px-3 py-1.5 hover:bg-pb-coral hover:text-white hover:border-pb-coral transition disabled:opacity-60"
                            title="Regenerate this recipe using the latest, nutritionally-balanced prompt"
                          >
                            {refreshingId === entry.buddy_id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3" />
                            )}
                            {refreshingId === entry.buddy_id ? "Refreshing…" : "Refresh recipe"}
                          </button>
                        )}
                        {!entry.isCustom && entry.recipe_id && (
                          <Link
                            to={`/recipes/${entry.recipe_id}`}
                            data-testid={`recipe-peek-full-${idx}`}
                            className="inline-flex items-center gap-1.5 text-xs font-extrabold text-pb-coral hover:gap-2 transition-all"
                          >
                            View full recipe page →
                          </Link>
                        )}
                        {entry.isCustom && !entry.buddy_id && (
                          <p className="text-xs text-pb-soft italic">
                            Custom Buddy — cook it any way your family loves.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Generated list */}
      {list && (
        <section id="shopping-list" className="max-w-5xl mx-auto px-5 sm:px-8 pb-24">
          <div className="bg-white rounded-[2.5rem] p-8 sm:p-12 shadow-[0_24px_60px_rgba(28,22,40,0.08)] print-card">
            <div className="flex items-center justify-between flex-wrap gap-3 no-print">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">Your week</p>
                <h3 className="font-display font-black text-3xl mt-1">
                  Shopping list ({list.items.length - crossedOff.size}/{list.items.length} to grab)
                </h3>
                <p className="text-pb-soft text-sm mt-1">
                  Tap an item to mark it as <em>already in your pantry</em> — printing skips those.
                </p>
              </div>
              <button
                data-testid="btn-print-list"
                onClick={printNeeded}
                className="inline-flex items-center gap-2 rounded-full bg-pb-cream text-pb-ink font-bold px-5 py-2.5 hover:bg-pb-sun transition border border-pb-sky/40 text-sm"
              >
                <Printer className="h-4 w-4" /> Print what's left
              </button>
            </div>

            {/* Print-only header — replaces the interactive header on paper. */}
            <div className="hidden print-only">
              <h3 className="font-display font-black text-2xl">Plate Buddies — Weekly Shopping List</h3>
              <p className="text-sm text-pb-soft mt-1">
                {list.items.length - crossedOff.size} items to grab · {crossedOff.size} already on hand
              </p>
            </div>

            <ul data-testid="list-shopping" className="mt-7 space-y-6">
              {(list.by_department && list.by_department.length > 0
                ? list.by_department
                : [{ department: "Shopping list", items: list.items }]
              ).map((group) => {
                const groupItems = group.items || [];
                const groupNeed = groupItems.filter(
                  (it) => !crossedOff.has(it.name)
                ).length;
                return (
                  <li
                    key={group.department}
                    data-testid={`dept-group-${group.department.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                    className="dept-group"
                  >
                    <div className="flex items-baseline justify-between gap-3 pb-3 border-b-2 border-dashed border-pb-coral/30 no-print">
                      <div className="inline-flex items-center gap-2">
                        <span
                          className="inline-flex items-center rounded-full bg-pb-coral text-white font-extrabold text-xs uppercase tracking-widest px-3 py-1"
                          data-testid={`dept-label-${group.department.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                        >
                          {group.department}
                        </span>
                        <span className="text-xs text-pb-soft font-bold">
                          {groupNeed} to grab · {groupItems.length} total
                        </span>
                      </div>
                    </div>
                    {/* Print-only compact department heading */}
                    <div className="hidden print-only pb-2 mt-2">
                      <p className="font-display font-black text-base uppercase tracking-widest text-pb-coral">
                        {group.department}
                      </p>
                    </div>
                    <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                      {groupItems.map((it, i) => {
                        const isCrossed = crossedOff.has(it.name);
                        return (
                          <li
                            key={`${group.department}-${i}`}
                            data-testid={`list-item-${it.name.toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 40)}`}
                            className={`shopping-item flex items-start gap-3 p-3 rounded-2xl transition cursor-pointer ${
                              isCrossed
                                ? "bg-pb-cream/60 opacity-70 is-crossed"
                                : "hover:bg-pb-cream"
                            }`}
                            onClick={() => toggleCrossed(it.name)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleCrossed(it.name);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isCrossed}
                          >
                            <span
                              className={`h-5 w-5 mt-0.5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition ${
                                isCrossed
                                  ? "border-pb-green bg-pb-green"
                                  : "border-pb-sky/60 bg-white"
                              }`}
                            >
                              <Check
                                className={`h-3 w-3 ${
                                  isCrossed ? "text-white" : "text-transparent"
                                }`}
                              />
                            </span>
                            <div className="flex-1">
                              <p
                                className={`font-bold leading-snug ${
                                  isCrossed ? "text-pb-soft line-through" : "text-pb-ink"
                                }`}
                              >
                                {it.name}
                              </p>
                              <p className="text-xs text-pb-soft mt-0.5">
                                for {(it.from || []).join(", ")}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>

            {/* Email me my plan */}
            <form data-testid="form-email-plan" onSubmit={emailPlan} className="mt-10 bg-pb-cream rounded-3xl p-5 sm:p-6 border border-pb-sky/40 flex flex-col sm:flex-row gap-3 items-center no-print">
              <Mail className="h-5 w-5 text-pb-coral flex-shrink-0" />
              <div className="flex-1 w-full">
                <p className="font-display font-black text-lg">Email me the whole list</p>
                <p className="text-sm text-pb-soft">
                  You'll get an inline preview <b className="text-pb-coral">plus a printable PDF</b> — big-print recipes for the fridge.
                </p>
              </div>
              <input
                data-testid="input-email-plan"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@family.com"
                className="rounded-2xl border-2 border-pb-sky/40 bg-white px-4 py-3 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral transition w-full sm:w-auto"
              />
              <button
                data-testid="btn-email-plan"
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-2xl bg-pb-ink text-white font-extrabold px-6 py-3 hover:bg-pb-coral transition disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Email it
              </button>
            </form>
          </div>
        </section>
      )}

      {/* Upsell */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-24 no-print">
        <div className="rounded-[2.5rem] bg-gradient-to-br from-pb-sun via-pb-coral to-pb-coral p-8 sm:p-12 text-white text-center">
          <p className="text-xs font-extrabold uppercase tracking-widest text-white/80">
            Prefer to plan hands-on?
          </p>
          <h3 className="font-display font-black text-3xl md:text-4xl mt-2 leading-tight">
            The whole family plans together with the Premium Kit.
          </h3>
          <p className="mt-3 text-white/90 max-w-xl mx-auto">
            Real day-of-the-week magnets + tiny Buddy magnets, so kids can pick tomorrow's dinner while you're pouring coffee.
          </p>
          <Link
            to="/products/premium-kit"
            data-testid="link-shop-kit"
            className="mt-6 inline-flex items-center justify-center rounded-full bg-white text-pb-coral font-extrabold px-7 py-4 hover:scale-105 transition shadow-[0_12px_28px_rgba(0,0,0,0.18)]"
          >
            Shop the Premium Kit
          </Link>
        </div>
      </section>

      {/* Printable cookbook — visible only when "Print recipes" is clicked. */}
      <section
        data-testid="recipe-cookbook"
        className="recipe-cookbook"
        aria-hidden="true"
      >
        <div className="cookbook-page">
          <div className="cookbook-masthead">
            <div className="cookbook-eyebrow">Plate Buddies — Weekly Recipe Book</div>
            <h2 className="cookbook-title">Your week of family meals</h2>
            <p className="cookbook-subtitle">
              {scheduledDays.length} {scheduledDays.length === 1 ? "recipe" : "recipes"} · clip to the fridge
            </p>
          </div>

          {scheduledDays.map((entry, i) => (
            <article key={i} className="cookbook-recipe">
              <div className="cookbook-recipe-head">
                <span className="cookbook-day">{entry.day}</span>
                <span className="cookbook-buddy">{entry.buddyName}</span>
              </div>
              <h3 className="cookbook-recipe-title">
                {entry.recipe.title || entry.buddyName}
              </h3>
              {(entry.recipe.time || entry.recipe.serves) && (
                <p className="cookbook-meta">
                  {[entry.recipe.time, entry.recipe.serves].filter(Boolean).join(" · ")}
                </p>
              )}
              {entry.recipe.story && (
                <p className="cookbook-story">{entry.recipe.story}</p>
              )}
              {entry.recipe.ingredients?.length > 0 && (
                <div className="cookbook-block">
                  <div className="cookbook-block-label">Ingredients</div>
                  <ul className="cookbook-ingredients">
                    {entry.recipe.ingredients.map((ing, j) => (
                      <li key={j}>{ing}</li>
                    ))}
                  </ul>
                </div>
              )}
              {entry.recipe.steps?.length > 0 && (
                <div className="cookbook-block">
                  <div className="cookbook-block-label">Steps</div>
                  <ol className="cookbook-steps">
                    {entry.recipe.steps.map((s, j) => (
                      <li key={j}>{s}</li>
                    ))}
                  </ol>
                </div>
              )}
              {entry.recipe.kid_task && (
                <div className="cookbook-callout cookbook-callout-kid">
                  <b>Kid task:</b> {entry.recipe.kid_task}
                </div>
              )}
              {entry.recipe.plating_idea && (
                <div className="cookbook-callout cookbook-callout-plating">
                  <b>Plating idea:</b> {entry.recipe.plating_idea}
                </div>
              )}
              {entry.recipe.family_tip && (
                <div className="cookbook-callout cookbook-callout-tip">
                  <b>Family tip:</b> {entry.recipe.family_tip}
                </div>
              )}
              {entry.isCustom && (entry.recipe.steps?.length ?? 0) === 0 && (
                <p className="cookbook-story" style={{ marginTop: "10px" }}>
                  A custom Buddy recipe — cook it any way your family loves!
                </p>
              )}
            </article>
          ))}

          <p className="cookbook-footer">
            Made with care · platebuddies.com
          </p>
        </div>
      </section>

      </EmailGate>
      <Footer />

      {/* Cook-day wizard — recipe-by-recipe kitchen view, mounted once so it
          can open over any part of the planner. */}
      <CookDayWizard
        open={cookDayOpen}
        onClose={() => setCookDayOpen(false)}
        scheduled={scheduledDays}
        startIndex={cookDayStart}
      />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        /* Cookbook is hidden by default — only renders for the "Print recipes" flow. */
        .recipe-cookbook { display: none; }

        @media print {
          /* SHOPPING-LIST PRINT MODE (default — body has no special class) */
          body:not(.print-mode-recipes) * { visibility: hidden !important; }
          body:not(.print-mode-recipes) .print-card,
          body:not(.print-mode-recipes) .print-card * { visibility: visible !important; }
          body:not(.print-mode-recipes) .no-print,
          body:not(.print-mode-recipes) .shopping-item.is-crossed { display: none !important; }
          body:not(.print-mode-recipes) .print-only { display: block !important; }
          body:not(.print-mode-recipes) .print-card {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            box-shadow: none !important;
            padding: 24px !important;
          }
          body:not(.print-mode-recipes) .print-card ul { display: block !important; }
          body:not(.print-mode-recipes) .print-card ul li { page-break-inside: avoid; }

          /* RECIPE-PRINT MODE — only the cookbook is shown. */
          body.print-mode-recipes * { visibility: hidden !important; }
          body.print-mode-recipes .recipe-cookbook,
          body.print-mode-recipes .recipe-cookbook * { visibility: visible !important; }
          body.print-mode-recipes .recipe-cookbook {
            display: block !important;
            position: absolute;
            inset: 0;
            background: #ffffff;
          }
          body.print-mode-recipes .cookbook-page {
            padding: 28px 32px;
            font-family: Georgia, "Times New Roman", serif;
            color: #1C1628;
          }
          body.print-mode-recipes .cookbook-masthead {
            border-bottom: 2px solid #F26A34;
            padding-bottom: 14px;
            margin-bottom: 22px;
          }
          body.print-mode-recipes .cookbook-eyebrow {
            font-family: -apple-system, "Segoe UI", sans-serif;
            font-size: 11px;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: #F26A34;
            font-weight: 800;
          }
          body.print-mode-recipes .cookbook-title {
            font-size: 28px;
            font-weight: 900;
            margin: 4px 0 6px 0;
            line-height: 1.15;
          }
          body.print-mode-recipes .cookbook-subtitle {
            font-family: -apple-system, "Segoe UI", sans-serif;
            font-size: 13px;
            color: #7c7e92;
            margin: 0;
          }
          body.print-mode-recipes .cookbook-recipe {
            page-break-inside: avoid;
            margin-bottom: 24px;
            padding-bottom: 18px;
            border-bottom: 1px dashed #d6d8e2;
          }
          body.print-mode-recipes .cookbook-recipe:last-of-type {
            border-bottom: none;
          }
          body.print-mode-recipes .cookbook-recipe-head {
            font-family: -apple-system, "Segoe UI", sans-serif;
            font-size: 11px;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            color: #7c7e92;
            font-weight: 800;
          }
          body.print-mode-recipes .cookbook-day { color: #F26A34; }
          body.print-mode-recipes .cookbook-buddy { margin-left: 10px; }
          body.print-mode-recipes .cookbook-recipe-title {
            font-size: 22px;
            font-weight: 900;
            margin: 6px 0 4px 0;
            line-height: 1.2;
          }
          body.print-mode-recipes .cookbook-meta {
            font-family: -apple-system, "Segoe UI", sans-serif;
            font-size: 12px;
            color: #9aa0b4;
            margin: 0 0 8px 0;
          }
          body.print-mode-recipes .cookbook-story {
            font-style: italic;
            font-size: 13px;
            color: #7c7e92;
            margin: 6px 0 10px 0;
          }
          body.print-mode-recipes .cookbook-block { margin-top: 10px; }
          body.print-mode-recipes .cookbook-block-label {
            font-family: -apple-system, "Segoe UI", sans-serif;
            font-size: 10px;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            color: #F26A34;
            font-weight: 800;
            margin-bottom: 4px;
          }
          body.print-mode-recipes .cookbook-ingredients,
          body.print-mode-recipes .cookbook-steps {
            font-family: -apple-system, "Segoe UI", sans-serif;
            margin: 0;
            padding-left: 20px;
            font-size: 13px;
            line-height: 1.55;
          }
          body.print-mode-recipes .cookbook-ingredients li,
          body.print-mode-recipes .cookbook-steps li { margin-bottom: 4px; }
          body.print-mode-recipes .cookbook-callout {
            font-family: -apple-system, "Segoe UI", sans-serif;
            margin-top: 8px;
            padding: 6px 10px;
            border-radius: 6px;
            font-size: 12px;
            line-height: 1.45;
          }
          body.print-mode-recipes .cookbook-callout-kid { background: #f0fafc; border-left: 3px solid #4DD0E1; }
          body.print-mode-recipes .cookbook-callout-plating { background: #fff3ec; border-left: 3px solid #F26A34; }
          body.print-mode-recipes .cookbook-callout-tip { background: #f1f9ec; border-left: 3px solid #81C784; }
          body.print-mode-recipes .cookbook-footer {
            font-family: -apple-system, "Segoe UI", sans-serif;
            text-align: center;
            font-size: 11px;
            color: #9aa0b4;
            margin-top: 20px;
          }
        }
        .print-only { display: none; }
      `}</style>
    </div>
  );
}
